import { beginLoginCompositionLibrary } from '../../quote-integration/loginCompositionLibrary';
import type { CurrentUser, SessionResult } from './generated/types';
import { ActiveApiError, type ActiveTransport } from './transport';

const renewable = (error: unknown) =>
  error instanceof ActiveApiError &&
  error.status === 401 &&
  (error.code === 'AUTHENTICATION_REQUIRED' ||
    error.code === 'SESSION_EXPIRED');

/** 仅启动时恢复；不替换已挂载的编辑身份，撤销/权限/网络错误不自动换身份。 */
export async function restoreInitialSession(options: {
  transport: ActiveTransport;
  csrf(): string | null;
  developmentLogin?: () => Promise<SessionResult>;
}): Promise<{ user: CurrentUser; csrfToken: string | null }> {
  const readVerifiedUser = async () => {
    // 启动时尚未挂载编辑身份；丢弃变化中的投影，只重读一次当前会话，不重放业务操作。
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const beforeCsrf = options.csrf();
      const user = await options.transport.getCurrentUser();
      const csrfToken = options.csrf();
      if (beforeCsrf === csrfToken) return { user, csrfToken };
    }
    throw new ActiveApiError(
      null,
      '登录正在由其他页面更新，请重新检查。',
      'SESSION_CHANGED_DURING_REVALIDATION',
    );
  };
  try {
    return await readVerifiedUser();
  } catch (error) {
    if (!renewable(error)) throw error;
    const csrf = options.csrf();
    if (csrf) {
      try {
        return await options.transport.refreshSession(csrf);
      } catch (refreshError) {
        if (!renewable(refreshError)) throw refreshError;
      }
    }
    if (!options.developmentLogin) throw error;
    const session = await options.developmentLogin();
    const { user, csrfToken } = await readVerifiedUser();
    if (
      user.id !== session.user.id ||
      user.companyId !== session.user.companyId
    )
      throw new ActiveApiError(401, '登录期间身份已变化，请重新检查。');
    beginLoginCompositionLibrary(user);
    return { user, csrfToken: csrfToken ?? session.csrfToken };
  }
}
